import { useEffect, useState, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { ArrowLeft, Printer } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TripPrintPage() {
  const { tripId } = useParams();
  const [trip, setTrip] = useState(null);
  const [buses, setBuses] = useState([]);
  const [tripStudents, setTripStudents] = useState([]);
  const [payments, setPayments] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const printRef = useRef();

  useEffect(() => {
    (async () => {
      try {
        const [tripRes, busRes, studentRes, paymentRes, expenseRes] = await Promise.all([
          axios.get(`${API}/trips/${tripId}`),
          axios.get(`${API}/trips/${tripId}/buses`),
          axios.get(`${API}/trips/${tripId}/students`),
          axios.get(`${API}/trips/${tripId}/payments`),
          axios.get(`${API}/trips/${tripId}/expenses`),
        ]);
        setTrip(tripRes.data);
        setBuses(busRes.data);
        setTripStudents(studentRes.data);
        setPayments(paymentRes.data);
        const expData = expenseRes.data;
        setExpenses(Array.isArray(expData) ? expData : expData.expenses || []);
      } catch {
        toast.error('Failed to load trip data');
      } finally {
        setLoading(false);
      }
    })();
  }, [tripId]);

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-500"></div>
      </div>
    );
  }

  if (!trip) return <p className="text-center py-16">Trip not found</p>;

  // Compute stats
  const boys = tripStudents.filter(s => s.gender === 'Male').length;
  const girls = tripStudents.filter(s => s.gender === 'Female').length;
  const totalCollected = payments.reduce((sum, p) => sum + Number(p.amount_paid || 0), 0);
  const totalBalance = payments.reduce((sum, p) => sum + Number(p.balance || 0), 0);
  const totalExpenses = expenses.reduce((sum, e) => sum + Number(e.amount || 0), 0);
  const profit = totalCollected - totalExpenses;

  // Group students by class
  const classesList = [...new Set(tripStudents.map(s => s.class_name || 'Unassigned'))].sort();

  return (
    <div>
      {/* Controls — hidden on print */}
      <div className="no-print flex items-center gap-3 mb-6">
        <Link to={`/trips/${tripId}`}>
          <Button variant="outline" size="sm">
            <ArrowLeft size={16} className="mr-1" /> Back
          </Button>
        </Link>
        <Button size="sm" className="bg-pink-500 hover:bg-pink-600 text-white" onClick={() => window.print()}>
          <Printer size={16} className="mr-1" /> Print
        </Button>
      </div>

      {/* Printable content */}
      <div ref={printRef}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 'bold', margin: 0 }}>{trip.title}</h1>
          <p style={{ margin: '0.25rem 0', color: '#555' }}>
            {trip.location} &bull; {trip.trip_date} &bull; {trip.departure_time} – {trip.return_time}
          </p>
          {trip.description && <p style={{ margin: '0.25rem 0', color: '#666', fontSize: '0.85rem' }}>{trip.description}</p>}
        </div>

        {/* Summary */}
        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '1.5rem' }}>
          <tbody>
            <tr>
              <td style={summaryCell}>Total Students: <strong>{tripStudents.length}</strong></td>
              <td style={summaryCell}>Boys: <strong>{boys}</strong></td>
              <td style={summaryCell}>Girls: <strong>{girls}</strong></td>
              <td style={summaryCell}>Fee/Student: <strong>${Number(trip.fee_per_student).toFixed(2)}</strong></td>
            </tr>
            <tr>
              <td style={summaryCell}>Collected: <strong>${totalCollected.toFixed(2)}</strong></td>
              <td style={summaryCell}>Balance: <strong>${totalBalance.toFixed(2)}</strong></td>
              <td style={summaryCell}>Expenses: <strong>${totalExpenses.toFixed(2)}</strong></td>
              <td style={summaryCell}>Profit: <strong style={{ color: profit >= 0 ? 'green' : 'red' }}>${profit.toFixed(2)}</strong></td>
            </tr>
          </tbody>
        </table>

        {/* Students by Bus */}
        <h2 style={sectionTitle}>Students by Bus</h2>
        {buses.map(bus => {
          const busStudents = tripStudents.filter(s => s.bus_id === bus.id);
          const busBoys = busStudents.filter(s => s.gender === 'Male').length;
          const busGirls = busStudents.filter(s => s.gender === 'Female').length;
          return (
            <div key={bus.id} style={{ marginBottom: '1.5rem' }}>
              <h3 style={{ fontSize: '0.95rem', fontWeight: 'bold', marginBottom: '0.3rem' }}>
                {bus.bus_name} — Driver: {bus.driver_name} | Capacity: {bus.capacity} | Assigned: {busStudents.length} (Boys: {busBoys}, Girls: {busGirls})
              </h3>
              {busStudents.length === 0 ? (
                <p style={{ color: '#999', fontSize: '0.8rem' }}>No students assigned</p>
              ) : (
                <table style={tableStyle}>
                  <thead>
                    <tr>
                      <th style={thStyle}>#</th>
                      <th style={thStyle}>Name</th>
                      <th style={thStyle}>Gender</th>
                      <th style={thStyle}>Grade</th>
                      <th style={thStyle}>Paid</th>
                      <th style={thStyle}>Balance</th>
                      <th style={thStyle}>Status</th>
                      <th style={thStyle}>Reference</th>
                    </tr>
                  </thead>
                  <tbody>
                    {busStudents.map((s, i) => (
                      <tr key={s.id}>
                        <td style={tdStyle}>{i + 1}</td>
                        <td style={tdStyle}>{s.full_name}</td>
                        <td style={tdStyle}>{s.gender || '—'}</td>
                        <td style={tdStyle}>{s.class_name || '—'}</td>
                        <td style={tdStyle}>${Number(s.paid || 0).toFixed(2)}</td>
                        <td style={tdStyle}>${Number(s.balance || 0).toFixed(2)}</td>
                        <td style={tdStyle}>{s.payment_status || '—'}</td>
                        <td style={tdStyle}>{s.payment_reference || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          );
        })}

        {/* Students by Grade */}
        <div className="page-break" />
        <h2 style={sectionTitle}>Students by Grade</h2>
        {classesList.map(cls => {
          const classStudents = tripStudents.filter(s => (s.class_name || 'Unassigned') === cls);
          const clsBoys = classStudents.filter(s => s.gender === 'Male').length;
          const clsGirls = classStudents.filter(s => s.gender === 'Female').length;
          return (
            <div key={cls} style={{ marginBottom: '1.5rem' }}>
              <h3 style={{ fontSize: '0.95rem', fontWeight: 'bold', marginBottom: '0.3rem' }}>
                {cls} — {classStudents.length} students (Boys: {clsBoys}, Girls: {clsGirls})
              </h3>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={thStyle}>#</th>
                    <th style={thStyle}>Name</th>
                    <th style={thStyle}>Gender</th>
                    <th style={thStyle}>Bus</th>
                    <th style={thStyle}>Paid</th>
                    <th style={thStyle}>Balance</th>
                    <th style={thStyle}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {classStudents.map((s, i) => {
                    const bus = buses.find(b => b.id === s.bus_id);
                    return (
                      <tr key={s.id}>
                        <td style={tdStyle}>{i + 1}</td>
                        <td style={tdStyle}>{s.full_name}</td>
                        <td style={tdStyle}>{s.gender || '—'}</td>
                        <td style={tdStyle}>{bus?.bus_name || '—'}</td>
                        <td style={tdStyle}>${Number(s.paid || 0).toFixed(2)}</td>
                        <td style={tdStyle}>${Number(s.balance || 0).toFixed(2)}</td>
                        <td style={tdStyle}>{s.payment_status || '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          );
        })}

        {/* Gender Summary */}
        <div className="page-break" />
        <h2 style={sectionTitle}>Gender Summary</h2>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Gender</th>
              <th style={thStyle}>Count</th>
              <th style={thStyle}>Fully Paid</th>
              <th style={thStyle}>Partially Paid</th>
              <th style={thStyle}>Not Paid</th>
            </tr>
          </thead>
          <tbody>
            {['Male', 'Female'].map(g => {
              const gs = tripStudents.filter(s => s.gender === g);
              return (
                <tr key={g}>
                  <td style={tdStyle}>{g}</td>
                  <td style={tdStyle}>{gs.length}</td>
                  <td style={tdStyle}>{gs.filter(s => s.payment_status === 'Fully Paid').length}</td>
                  <td style={tdStyle}>{gs.filter(s => s.payment_status === 'Partially Paid').length}</td>
                  <td style={tdStyle}>{gs.filter(s => s.payment_status === 'Not Paid').length}</td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {/* Expenses */}
        {expenses.length > 0 && (
          <>
            <h2 style={{ ...sectionTitle, marginTop: '1.5rem' }}>Expenses</h2>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>#</th>
                  <th style={thStyle}>Type</th>
                  <th style={thStyle}>Amount</th>
                  <th style={thStyle}>Description</th>
                  <th style={thStyle}>Date</th>
                </tr>
              </thead>
              <tbody>
                {expenses.map((e, i) => (
                  <tr key={e.id}>
                    <td style={tdStyle}>{i + 1}</td>
                    <td style={tdStyle}>{e.expense_type}</td>
                    <td style={tdStyle}>${Number(e.amount).toFixed(2)}</td>
                    <td style={tdStyle}>{e.description || '—'}</td>
                    <td style={tdStyle}>{e.expense_date}</td>
                  </tr>
                ))}
                <tr>
                  <td style={tdStyle} colSpan={2}><strong>Total</strong></td>
                  <td style={tdStyle}><strong>${totalExpenses.toFixed(2)}</strong></td>
                  <td style={tdStyle} colSpan={2}></td>
                </tr>
              </tbody>
            </table>
          </>
        )}
      </div>
    </div>
  );
}

// Inline styles for print-friendly rendering
const summaryCell = { border: '1px solid #ccc', padding: '6px 10px', fontSize: '0.85rem' };
const sectionTitle = { fontSize: '1.1rem', fontWeight: 'bold', marginBottom: '0.5rem', borderBottom: '2px solid #333', paddingBottom: '0.25rem' };
const tableStyle = { width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem', marginBottom: '0.5rem' };
const thStyle = { border: '1px solid #999', padding: '4px 8px', textAlign: 'left', backgroundColor: '#f5f5f5', fontWeight: 'bold', fontSize: '0.75rem' };
const tdStyle = { border: '1px solid #ccc', padding: '4px 8px', fontSize: '0.75rem' };
